import { Cpu, Money, TextColumns } from "@phosphor-icons/react";
import {
  Tooltip,
  TooltipAnchor,
  TooltipArrow,
  TooltipProvider,
} from "@replicate/ui";
import { sortBy, uniq } from "lodash-es";
import type { ReactNode } from "react";
import { P, match } from "ts-pattern";
import {
  BillingMetric,
  type BillingConfig,
  type BillingConfigTiers,
  type Version,
} from "../types";

export default function ModelPricePopover({
  hardware,
  billingConfig,
  p50price,
  price,
  version,
}: {
  hardware: string;
  billingConfig: BillingConfig | null;
  p50price: string | null;
  price: string | null;
  version: Version | null;
}) {
  const anchorClassName = "text-r8-sm flex gap-2 [&>svg]:mt-0.5";
  const anchorTitleClassName = "inline-flex items-center gap-1";

  if (version && billingConfig?.current_tiers) {
    // We assume the model with a billing config has a version
    const tiers = billingConfig.current_tiers;

    // If there's only one tier with one price, show the specific billing unit
    if (tiers.length === 1 && tiers[0].prices.length === 1) {
      const price = tiers[0].prices[0];
      const anchor = (
        <div className={anchorClassName}>
          <Money size={18} className="flex-shrink-0" />
          <div className={anchorTitleClassName}>
            {price.metric === BillingMetric.UnspecifiedBillingMetric ? (
              <>Priced {price.title}</>
            ) : (
              <>
                {price.price} {price.title}
              </>
            )}
          </div>
        </div>
      );

      return (
        <ModelPriceTooltip anchor={anchor}>
          <p>
            This model is priced by {price.metric_display}.{" "}
            {price.metric === BillingMetric.UnspecifiedBillingMetric ? (
              <>
                View <a href="#pricing">pricing details</a> for a full overview
              </>
            ) : (
              <>
                It costs{" "}
                <span className="font-semibold">
                  {price.price} {price.title}
                </span>
                {price.description && <>, {price.description}</>}
              </>
            )}
            .
          </p>
        </ModelPriceTooltip>
      );
    }

    // For multiple tiers or prices, show the "multiple properties" view
    const metrics = collectMetrics(tiers);
    const formattedMetrics = formatCommaList(metrics);

    const anchor = (
      <div className={anchorClassName}>
        <TextColumns size={18} className="flex-shrink-0" />
        <div className={anchorTitleClassName}>
          Priced by multiple properties
        </div>
      </div>
    );

    return (
      <ModelPriceTooltip anchor={anchor}>
        <p>
          {formattedMetrics && (
            <>This model is priced per {formattedMetrics}.</>
          )}{" "}
          {version && <a href="#pricing">View more.</a>}
        </p>
      </ModelPriceTooltip>
    );
  }

  // For non-official models, show hardware pricing
  const anchor = (
    <div className={anchorClassName}>
      <Cpu size={18} className="flex-shrink-0" />
      <div className={anchorTitleClassName}>
        {hardware || "No hardware selected"}
      </div>
    </div>
  );

  return (
    <ModelPriceTooltip anchor={anchor} linkToPricing={!!version}>
      <div className="space-y-2">
        {match({ p50price, price, hardware })
          .with({ hardware: "", p50price: null, price: null }, () => (
            <p>
              This model does not have a current version with hardware selected.
            </p>
          ))
          .with({ p50price: null, price: null }, () => (
            <p>This model runs on {hardware}.</p>
          ))
          .with({ p50price: P.not(null) }, ({ p50price }) => (
            <p>
              This model costs approximately{" "}
              <span className="font-semibold">{p50price}</span> to run on
              Replicate, but this varies depending on your inputs.{" "}
            </p>
          ))
          .with({ price: P.not(null) }, ({ price }) => (
            <p>
              This model runs on {hardware} hardware which costs{" "}
              <span className="font-semibold">{price}</span>
            </p>
          ))
          .exhaustive()}
      </div>
    </ModelPriceTooltip>
  );
}

function ModelPriceTooltip({
  anchor,
  children,
  linkToPricing = true,
}: {
  anchor: ReactNode;
  children: ReactNode;
  linkToPricing?: boolean;
}) {
  return (
    <TooltipProvider showTimeout={0}>
      <TooltipAnchor
        render={
          linkToPricing ? (
            /* biome-ignore lint/a11y/useAnchorContent: handled by ariakit */
            <a className="link inline-block" href="#pricing" />
          ) : (
            <span className="underline decoration-dotted cursor-help" />
          )
        }
      >
        {anchor}
      </TooltipAnchor>
      <Tooltip className="max-w-56 text-r8-xs z-50">
        <TooltipArrow />
        {children}
      </Tooltip>
    </TooltipProvider>
  );
}

export function formatCommaList(items: string[]): string | null {
  const filteredItems = items.filter((item) => item !== "run");

  if (filteredItems.length === 0) {
    return null;
  }

  if (filteredItems.length === 1) {
    return items[0];
  }

  if (filteredItems.length === 2) {
    return `${filteredItems[0]} and ${filteredItems[1]}`;
  }

  return `${filteredItems.slice(0, -1).join(", ")}, and ${filteredItems.at(
    -1
  )}`;
}

export function collectMetrics(tiers: BillingConfigTiers): string[] {
  const metrics = uniq(
    tiers.flatMap((tier) => tier.prices.map((price) => price.metric_display))
  );

  return sortBy(metrics);
}
